import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import useFetch from 'use-http';
import Select from 'react-select';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { ITableConfig } from 'src/components/common/table';
import { ODataFilter, ODataOptions, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';

interface IPayPeriodRangeDialogProps {
    dialogName: string;
    // Resolves to the same status text CommissionPage should show in the MSG bar once the whole
    // range has finished building (Oracle's own :pp_range.msg has no web equivalent for live
    // per-period updates - see PayPeriodRangeDialog/CommissionPage.doBuildPeriodRange).
    onBuild: (begPeriod: number, endPeriod: number) => Promise<string>;
    onReports: (begPeriod: number, endPeriod: number) => void;
    onClose: () => void;
}

interface IPeriodOptionType {
    value: number;
    label: string;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem' };
const compactSelectStyles = {
    control: (provided: React.CSSProperties) => ({ ...provided, minHeight: '3rem', height: '3rem' }),
    valueContainer: (provided: React.CSSProperties) => ({ ...provided, height: '3rem', padding: '0 8px' }),
    input: (provided: React.CSSProperties) => ({ ...provided, margin: '0', padding: '0' }),
    indicatorsContainer: (provided: React.CSSProperties) => ({ ...provided, height: '3rem' }),
    menu: (provided: React.CSSProperties) => ({ ...provided, zIndex: 999 }),
};

// Mirrors manager_commission.fmt's "PP_Range" window ("Multiple Periods" button, oid 46,
// Go_Block('PP_Range')) - Beginning/Ending Period, Build (loops CommissionService.BuildPeriodRange
// across the range - see CommissionPage.doBuildPeriodRange), Reports (drills into RANGE_PARMS, a
// separate params screen for the older MarginsByManager/MarginsByProject report pair - see
// CommissionPage.openMarginsReport), Cancel, and a status MSG bar (Oracle's own :pp_range.msg).
const PayPeriodRangeDialog = (props: IPayPeriodRangeDialogProps) => {
    const [msg, setMsg] = useState<string>('');
    const [isBuilding, setIsBuilding] = useState<boolean>(false);

    // Beginning/Ending Period combos - same debounced-search Select pattern as CommissionSearch's
    // own Period No. field (which itself replaced the old FIND_PP popup): server-side search via
    // AccountsPayable/payperiods, typing either "202606" or "2026-06" matches, initial options
    // default to periods within ~6 months of now. A single fetched options list serves both
    // fields - whichever one the user is actively typing in just reloads it. Since a value can only
    // ever come from this list, there's no more need for the old KEY-NEXT-ITEM-style "this period
    // does not exist" blur validation - an invalid period simply isn't selectable anymore.
    const { get: getPayPeriods } = useFetch();
    const [periodOptions, setPeriodOptions] = useState<Array<IPeriodOptionType>>([]);
    const [selectedBegPeriod, setSelectedBegPeriod] = useState<IPeriodOptionType | null>(null);
    const [selectedEndPeriod, setSelectedEndPeriod] = useState<IPeriodOptionType | null>(null);

    const insertDash = (periodNumber: number) => {
        const str = periodNumber.toString();
        const insertIndex = str.length - 2;
        return str.slice(0, insertIndex) + '-' + str.slice(insertIndex);
    };

    const normalizePeriodInput = (str: string) => str.replace(/-/g, '');

    const loadPeriodOptions = async (searchTerm: string) => {
        const tableConfig: ITableConfig = { rowCount: 100, idField: 'Id', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.clearFilters();
        options.addPaging(1, 50);
        options.addOrderBy('Id', ODataOrderByDirection.Descending);

        const numericTerm = Number(normalizePeriodInput(searchTerm || ''));
        if (searchTerm && !isNaN(numericTerm)) {
            options.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, numericTerm));
        } else {
            const now = new Date();
            const begDate = new Date(new Date().setMonth(now.getMonth() - 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(new Date().setMonth(now.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            options.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThan, endDate));
            options.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThan, begDate));
        }

        const result = await getPayPeriods(`/v1.0/AccountsPayable/payperiods${options.toString()}`);
        if (Array.isArray(result?.items)) {
            setPeriodOptions(result.items.map((pp: { periodNumber: number }) => ({
                value: pp.periodNumber,
                label: insertDash(pp.periodNumber),
            })));
        }
    };

    const periodSearch = _.debounce((inputText: string) => loadPeriodOptions(inputText), 300);

    useEffect(() => {
        loadPeriodOptions('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const close = () => {
        setMsg('');
        props.onClose();
    };

    const handleBuild = async () => {
        if (!selectedBegPeriod?.value || !selectedEndPeriod?.value) {
            setMsg('Please enter a beginning and ending period.');
            return;
        }
        setIsBuilding(true);
        setMsg('');
        try {
            const resultMsg = await props.onBuild(selectedBegPeriod.value, selectedEndPeriod.value);
            setMsg(resultMsg);
        } catch {
            setMsg('Unable to build. Please try again.');
        } finally {
            setIsBuilding(false);
        }
    };

    const handleReports = () => {
        if (!selectedBegPeriod?.value || !selectedEndPeriod?.value) {
            setMsg('Please enter a beginning and ending period.');
            return;
        }
        props.onReports(selectedBegPeriod.value, selectedEndPeriod.value);
    };

    return (
        <Dialog title="Multiple Periods" dialogName={props.dialogName} bodyStyle={{ width: '34%' }} isExitButtonInvisible={true}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <label style={{ ...labelStyle, textAlign: 'right', marginRight: '1.25rem' }}>Beginning Period</label>
                <div style={{ width: '14rem', flexShrink: 0 }}>
                    <Select
                        placeholder="Period#"
                        options={periodOptions}
                        value={selectedBegPeriod}
                        onChange={(option: IPeriodOptionType | null) => setSelectedBegPeriod(option)}
                        onInputChange={(inputText: string) => periodSearch(inputText)}
                        filterOption={(option, inputValue) => (
                            normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                        )}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={isBuilding}
                        styles={compactSelectStyles}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '.75rem' }}>
                <label style={{ ...labelStyle, textAlign: 'right', marginRight: '1.25rem' }}>Ending Period</label>
                <div style={{ width: '14rem', flexShrink: 0 }}>
                    <Select
                        placeholder="Period#"
                        options={periodOptions}
                        value={selectedEndPeriod}
                        onChange={(option: IPeriodOptionType | null) => setSelectedEndPeriod(option)}
                        onInputChange={(inputText: string) => periodSearch(inputText)}
                        filterOption={(option, inputValue) => (
                            normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                        )}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={isBuilding}
                        styles={compactSelectStyles}
                    />
                </div>
            </div>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center', gap: '1rem' }}>
                <Button className="blue-hover-button" style={{ width: '30%' }} text={isBuilding ? 'Building...' : 'Build'} loading={isBuilding} onClick={handleBuild} disabled={isBuilding} />
                <Button className="blue-hover-button" style={{ width: '30%' }} text="Reports" onClick={handleReports} disabled={isBuilding} />
                <Button className="blue-hover-button" style={{ width: '30%' }} text="Cancel" onClick={() => close()} disabled={isBuilding} />
            </div>
            {!!msg && (
                <div style={{ marginTop: '1rem', textAlign: 'center', color: 'red' }}>
                    {msg}
                </div>
            )}
        </Dialog>
    );
};

export default PayPeriodRangeDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/PayPeriodRangeDialog.tsx